import type { PublicSession } from '../api/types';
import { availableCurrencies, formatDisplay, rememberDisplayCurrency } from '../currency';
import { t } from '../i18n';
import { formatDateTime, formatFiat } from '../utils/format';
import { LanguageSwitch } from './LanguageSwitch';

interface Props {
  session: PublicSession;
  displayCurrency: string;
  onDisplayCurrency(code: string): void;
}

/**
 * Merchant identity, the amount due (always in the merchant's currency) and, when the API
 * sent `display_rates`, an approximate figure in the payer's chosen currency with a selector.
 */
export function Header({ session, displayCurrency, onDisplayCurrency }: Props) {
  const initials = session.merchant.name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  const rates = session.display_rates ?? null;
  const currencies = rates ? availableCurrencies(session.currency, rates) : [];
  const showSelector = currencies.length > 1;
  const sessionCcy = session.currency.toUpperCase();
  const approx = showSelector && displayCurrency !== sessionCcy ? formatDisplay(session.amount, rates, displayCurrency) : null;
  const due = formatFiat(session.amount, session.currency);

  return (
    <header class="cv-header">
      {session.merchant.logo_url ? (
        <img src={session.merchant.logo_url} alt="" referrerpolicy="no-referrer" />
      ) : (
        <div class="cv-logo-fallback" aria-hidden="true">
          {initials}
        </div>
      )}
      <div class="cv-merchant">
        <h1>{t('summary.payTo', { merchant: session.merchant.name })}</h1>
        {session.description ? (
          <p>{session.description}</p>
        ) : (
          <p class="mono">
            {t('summary.orderRef')}: {session.id}
          </p>
        )}
      </div>
      <div class="cv-total">
        <div class="cv-amount">{due}</div>
        <div class="cv-currency">{t('summary.total')}</div>
        {showSelector && (
          <div class="cv-approx-row">
            {approx && (
              <span
                class="cv-approx"
                title={t('summary.approxTitle', { amount: due, asOf: rates?.as_of ? formatDateTime(rates.as_of) : '' })}
              >
                {t('summary.approx', { amount: approx })}
              </span>
            )}
            <label class="cv-switch compact cv-ccy" title={t('app.currency')}>
              <span class="sr-only">{t('app.currency')}</span>
              <select
                id="cv-display-currency"
                aria-label={t('app.currency')}
                value={displayCurrency}
                onChange={(e) => {
                  const code = (e.currentTarget as HTMLSelectElement).value;
                  rememberDisplayCurrency(code);
                  onDisplayCurrency(code);
                }}
              >
                {currencies.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <span class="val" aria-hidden="true">
                {displayCurrency}
              </span>
            </label>
          </div>
        )}
      </div>
      <div class="cv-header-tools">
        <LanguageSwitch compact />
      </div>
    </header>
  );
}
